import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarBlank, CreditCard, Star, CheckCircle, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { toDisplayUrl } from "@/components/PhotoUpload";
import { StarRow } from "@/components/Stars";
import MessagesDialog from "@/components/MessagesDialog";

export default function GuestDashboard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [msgBooking, setMsgBooking] = useState(null);
  const [unread, setUnread] = useState({});

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: u }] = await Promise.all([api.get("/bookings/mine"), api.get("/messages/unread")]);
    setBookings(data);
    setUnread(u || {});
    setLoading(false);
    // Load which bookings already have a review
    const reviewed = new Set();
    for (const b of data) {
      if (b.listing?.id) {
        try {
          const { data: rs } = await api.get(`/listings/${b.listing.id}/reviews`);
          if (rs.some((r) => r.booking_id === b.id)) reviewed.add(b.id);
        } catch {}
      }
    }
    setReviewedIds(reviewed);
  };

  useEffect(() => { load(); }, []);

  const pay = async (b) => {
    setPayingId(b.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: b.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPayingId(null);
    }
  };

  const submitReview = async () => {
    if (!reviewFor) return;
    try {
      await api.post(`/bookings/${reviewFor.id}/review`, { stars, comment });
      toast.success("Review posted");
      setReviewFor(null);
      setComment(""); setStars(5);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= today && b.status !== "declined");
  const past = bookings.filter((b) => b.date < today || b.status === "declined");

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="border-b border-neutral-200 pb-8 mb-8">
        <div className="label-eyebrow mb-2">Guest</div>
        <h1 className="font-display font-medium text-4xl tracking-tight">Your trips</h1>
      </div>

      <Section title="Upcoming" items={upcoming} empty="Nothing upcoming — go find a den." loading={loading}
        renderActions={(b) => (
          <>
            <Button
              data-testid={`msg-${b.id}`}
              variant="outline"
              onClick={() => setMsgBooking(b)}
              className="h-9 border-neutral-300 relative"
            >
              <ChatCircle size={14} className="mr-1"/> Message
              {unread[b.id] > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] bg-red-600 text-white rounded-full flex items-center justify-center">{unread[b.id]}</span>}
            </Button>
            {b.status === "confirmed" && b.payment_status !== "paid" && (
              <Button
                data-testid={`pay-${b.id}`}
                onClick={() => pay(b)}
                disabled={payingId === b.id}
                className="h-9 bg-neutral-900 hover:bg-neutral-800 text-white"
              >
                <CreditCard size={14} className="mr-1"/> {payingId === b.id ? "Redirecting..." : "Pay to secure"}
              </Button>
            )}
            {b.status === "confirmed" && b.payment_status === "paid" && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={14} weight="fill"/> Paid</span>
            )}
          </>
        )}
      />
      <div className="h-12"/>
      <Section title="Past" items={past} empty="No past bookings." loading={loading}
        renderActions={(b) => (
          (b.status === "confirmed" || b.status === "completed") && !reviewedIds.has(b.id) ? (
            <Button
              data-testid={`review-${b.id}`}
              variant="outline"
              onClick={() => { setReviewFor(b); setStars(5); setComment(""); }}
              className="h-9 border-neutral-300"
            >
              <Star size={14} className="mr-1"/> Leave a review
            </Button>
          ) : reviewedIds.has(b.id) ? (
            <span className="text-xs text-neutral-500">Reviewed</span>
          ) : null
        )}
      />

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-medium text-2xl">How was it?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-neutral-600">{reviewFor?.listing?.title}</div>
            <div>
              <div className="label-eyebrow mb-2">Rating</div>
              <StarRow value={stars} size={26} onChange={setStars} testIdPrefix="review-star"/>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Note (optional)</div>
              <Textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What worked? What could be better?"
                className="border-neutral-300 min-h-24"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-neutral-300" onClick={() => setReviewFor(null)}>Cancel</Button>
              <Button data-testid="submit-review" onClick={submitReview} className="bg-neutral-900 hover:bg-neutral-800">Post review</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MessagesDialog open={!!msgBooking} onClose={() => { setMsgBooking(null); load(); }} booking={msgBooking}/>
    </div>
  );
}

function Section({ title, items, empty, loading, renderActions }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{title}</div>
      </div>
      {loading ? (
        <div className="border border-neutral-200 h-32 animate-pulse bg-neutral-50"/>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-12 text-center text-neutral-500 text-sm flex flex-col items-center gap-3">
          <CalendarBlank size={22}/>
          {empty}
          <Link to="/browse" className="text-neutral-900 underline text-sm">Browse listings</Link>
        </div>
      ) : (
        <div className="border border-neutral-200 divide-y divide-neutral-200">
          {items.map((b) => (
            <div key={b.id} data-testid={`trip-${b.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
              <img src={toDisplayUrl(b.listing?.photos?.[0])} alt="" className="w-24 h-16 object-cover border border-neutral-200"/>
              <div className="flex-1 min-w-0">
                <Link to={`/listings/${b.listing?.id}`} className="font-medium hover:underline">
                  {b.listing?.title}
                </Link>
                <div className="text-sm text-neutral-500">
                  {b.date} · {String(b.start_hour).padStart(2,"0")}:00–{String(b.end_hour).padStart(2,"0")}:00 · {b.listing?.neighborhood}
                </div>
              </div>
              <StatusPill s={b.status}/>
              <div className="font-medium">${b.total_price}</div>
              {renderActions && renderActions(b)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ s }) {
  const colors = {
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-red-50 text-red-700 border-red-200",
    completed: "bg-neutral-100 text-neutral-700 border-neutral-200",
    requested: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <span className={`text-[11px] uppercase tracking-widest px-2 py-1 border ${colors[s] || ""}`}>{s}</span>;
}
